import { useCallback, useEffect, useMemo, useRef } from "react";
import { capture } from "@/lib/posthog";
import { useConnectionsList } from "@fastn-ai-team/connector-ui";
import { useAuth } from "@/lib/auth";
import { hasMinRole } from "@/lib/rbac";
import { canViewCustomerConnections } from "@/lib/customer-connections-view";
import { useWorkflows } from "./use-workflows";
import { useWidgets } from "./use-widgets";
import { useInstallations } from "./use-installations";
import { useSaasConnectors } from "./use-saas-connectors";
import { useClaimOnboardingCredits, useSetupJourney } from "./use-setup-journey";
import { useHubSettings } from "./use-hub-settings";

/**
 * The steps a partner takes from an empty org to a customer on a live
 * integration, with the state each one is really in for this org.
 *
 *   product       their own product is registered as a connector (Your
 *                 product), so customer connections to it can exist
 *   integrations  the integrations the widget will offer are chosen: a
 *                 widget of their own with at least one connector, the
 *                 widget's Catalog connectors feature on, or a workflow built
 *   embed         the widget is in their product: it has loaded on a page of
 *                 the partner's own (setupJourney.widgetLoadedAt, stamped by
 *                 the api when it serves the iframe), or a customer has
 *                 connected, which can only happen through it
 *   customers     a customer connected an app in the widget: an active
 *                 customer connection to anything but the partner's own
 *                 product, or an active installation, or the api's stamp of
 *                 the first one (setupJourney.customerConnectedAt)
 *
 * Read from real resources rather than a stored checklist, so the cards tick
 * themselves as the org works and can never nag about work already done.
 * Three outcomes per step: a boolean is an answer, `null` is a read that
 * failed, `undefined` is a read that has not happened yet.
 */
export type SetupStepId = "product" | "integrations" | "embed" | "customers";
export type SetupStepState = "done" | "next" | "waiting";

export interface SetupStep {
  id: SetupStepId;
  state: SetupStepState;
  /** True when the read behind this step failed, so the card can say so. */
  unknown: boolean;
  /** True when this role cannot do the step itself (an admin has to). */
  needsAdmin: boolean;
}

export interface SetupProgress {
  steps: SetupStep[];
  /** All done: the org has a customer on a live integration. */
  complete: boolean;
  isLoading: boolean;
  /** How many customer connections are active, for the last card's line. */
  customerConnections: number | null;
  /** Re-read every source, so a step done elsewhere (in the partner's AI
   *  tool, another tab) ticks over without a reload. */
  refresh: () => void;
}

/** Enough customer connections to tell an app connection from the product's own. */
const CONNECTIONS_READ = 100;

/**
 * Mount only for the roles that can set up (developer and up): one of its
 * reads, useConnectionsList, has no off switch, so a gate inside would still
 * fire it for everyone.
 */
export function useSetupProgress(): SetupProgress {
  const { userContext } = useAuth();
  const role = userContext?.role ?? "end_user";
  // Registering the product is admin work, and the list is admin-gated.
  const canReadProduct = hasMinRole(role, "admin");
  const customersScope = canViewCustomerConnections({ role: userContext?.role, orgType: userContext?.orgType });

  const product = useSaasConnectors({ enabled: canReadProduct });
  const workflows = useWorkflows();
  const widgets = useWidgets(undefined, { ownOnly: true });
  const hub = useHubSettings();
  const journey = useSetupJourney();
  const installations = useInstallations({});
  const customerConnections = useConnectionsList({
    status: "ACTIVE",
    limit: CONNECTIONS_READ,
    offset: 0,
    scope: customersScope ? "customers" : "saasPool",
  });

  // refetch functions are stable per observer. Only sources this role may
  // read: refetch ignores `enabled`, so a disabled one would fetch and 403.
  const refresh = useCallback(() => {
    void workflows.refetch();
    void widgets.refetch();
    void installations.refetch();
    void customerConnections.refetch();
    void journey.refetch();
    if (canReadProduct) void product.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadProduct]);

  const base = useMemo(() => {
    const seen = (q: { isSuccess: boolean; isError: boolean }, value: boolean): boolean | null | undefined =>
      q.isSuccess ? value : q.isError ? null : undefined;

    const registered = canReadProduct ? seen(product, (product.data?.length ?? 0) > 0) : undefined;
    const widgetOffers = seen(widgets, (widgets.data ?? []).some((w) => (w.connectorIds?.length ?? 0) > 0));
    const built = seen(workflows, (workflows.data?.length ?? 0) > 0);
    const catalogOn = hub.data?.showCommunityConnectors === true;
    const loaded = seen(journey, !!journey.data?.widgetLoadedAt);
    // A customer's installation only: the agent testing a workflow installs it
    // in the partner's own org (endOrgId = orgId), which is not a customer.
    const activeInstalls = (installations.data ?? []).filter((i) => i.status === "active" && i.endOrgId !== i.orgId).length;
    // A customer's connection to the partner's OWN product is made by the
    // partner's backend (step 3), not by the customer in the widget, so it
    // does not tick step 4. Only an admin can read which connector is the
    // product; for anyone else every customer connection counts.
    const productIds = new Set((product.data ?? []).map((p) => p.connectorId));
    const rows = customerConnections.data?.connections ?? [];
    const appRows = rows.filter((c) => !productIds.has(c.connectorId)).length;
    const moreUnread = (customerConnections.data?.total ?? 0) > rows.length;
    const customerTotal = customersScope
      ? customerConnections.isSuccess ? appRows + (moreUnread && appRows === 0 ? 1 : 0) : null
      : 0;

    const chosen: boolean | null | undefined =
      widgetOffers === true || built === true || catalogOn ? true
        : (widgetOffers === undefined || built === undefined) ? undefined
          : (widgetOffers === null && built === null) ? null : false;
    // The api's stamp holds even for a reader who cannot list customer connections.
    const connected: boolean | null | undefined =
      journey.data?.customerConnectedAt ? true
        : customerTotal === null
          ? (customerConnections.isError ? null : undefined)
          : customerTotal > 0 || activeInstalls > 0;
    const embedded: boolean | null | undefined = loaded === true || connected === true ? true : loaded;

    const raw: Array<[SetupStepId, boolean | null | undefined, boolean]> = [
      ["product", registered, !canReadProduct],
      ["integrations", chosen, false],
      ["embed", embedded, false],
      ["customers", connected, false],
    ];
    // A step this role cannot do (connecting the product is admin work) never
    // becomes the current step: it waits on an admin, and the next step the
    // reader can do takes its place, so a developer is never stuck on step 1.
    let nextAssigned = false;
    const steps: SetupStep[] = raw.map(([id, v, needsAdmin]) => {
      if (v === true) return { id, state: "done", unknown: false, needsAdmin: false };
      if (needsAdmin) return { id, state: "waiting", unknown: false, needsAdmin: true };
      const state: SetupStepState = nextAssigned ? "waiting" : "next";
      nextAssigned = true;
      return { id, state, unknown: v === null, needsAdmin };
    });

    const queries = [workflows, widgets, installations, customerConnections, journey, ...(canReadProduct ? [product] : [])];
    const isLoading = queries.some((q) => q.isPending && q.fetchStatus === "fetching");

    return {
      steps,
      // A step this role cannot read (connecting the product) does not hold
      // setup open: a developer cannot see whether an admin has done it.
      complete: steps.every((s) => s.state === "done" || s.needsAdmin),
      isLoading,
      customerConnections: customerTotal,
    };
    // Primitives only: React Query returns a fresh tracked proxy each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    product.isSuccess, product.isError, product.data, product.isPending, product.fetchStatus,
    workflows.isSuccess, workflows.isError, workflows.data?.length, workflows.isPending, workflows.fetchStatus,
    widgets.isSuccess, widgets.isError, widgets.data, widgets.isPending, widgets.fetchStatus, hub.data,
    journey.isSuccess, journey.isError, journey.data?.widgetLoadedAt, journey.data?.customerConnectedAt, journey.isPending, journey.fetchStatus,
    installations.isSuccess, installations.isError, installations.data, installations.isPending, installations.fetchStatus,
    customerConnections.isSuccess, customerConnections.isError, customerConnections.data, customerConnections.isPending, customerConnections.fetchStatus,
    canReadProduct, customersScope,
  ]);

  useStepDoneEvents(base.steps, base.isLoading, userContext?.orgId, journey.data?.status, {
    connectorCount: journey.data?.connectorIds.length ?? 0,
    customerConnections: base.customerConnections,
  });

  // A step ticking over is when its credits are due. The server re-checks the
  // records and grants once per step, so asking again is harmless; this only
  // keeps it to once per newly done step for this mount.
  const claim = useClaimOnboardingCredits();
  const doneCount = base.steps.filter((s) => s.state === "done").length;
  const grantCount = journey.data?.creditGrants?.length ?? 0;
  const claimedAt = useRef(0);
  useEffect(() => {
    if (!journey.isSuccess || doneCount <= grantCount || doneCount <= claimedAt.current) return;
    claimedAt.current = doneCount;
    claim.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey.isSuccess, doneCount, grantCount]);

  return useMemo(() => ({ ...base, refresh }), [base, refresh]);
}

const NEXT: Record<SetupStepId, "integrations" | "embed" | "customers" | null> = {
  product: "integrations", integrations: "embed", embed: "customers", customers: null,
};

/** onboarding_completed is sent once per org: by Finish on the guided screens or,
 *  when the last step was done elsewhere, by useStepDoneEvents. Both check this. */
export function claimOnboardingCompleted(orgId: string | undefined): boolean {
  if (!orgId) return true;
  const key = `fastn.onboardingCompleted:${orgId}`;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, "1");
  } catch {
    // No storage: send it; a rare double beats a finish that never counts.
  }
  return true;
}

/**
 * setup_step_done, once per step for this person: the first time the app sees
 * the step done, wherever it was done (here, in the partner's AI tool, or
 * while they were away). Remembered per org in this browser, so the many
 * places that read progress send it once. Not for a journey that was already
 * complete, which would replay every org's history on first load.
 *
 * And onboarding_completed when all four are done, for a person who finished
 * outside the guided screens and so never pressed Finish.
 */
export function useStepDoneEvents(
  steps: SetupStep[],
  loading: boolean,
  orgId: string | undefined,
  status: string | undefined,
  done: { connectorCount: number; customerConnections: number | null },
): void {
  const doneKey = steps.filter((s) => s.state === "done").map((s) => s.id).join(",");
  useEffect(() => {
    if (loading || !orgId || !status || status === "completed") return;
    for (const id of doneKey ? doneKey.split(",") as SetupStepId[] : []) {
      const key = `fastn.setupStepDone:${orgId}:${id}`;
      try {
        if (localStorage.getItem(key)) continue;
        localStorage.setItem(key, "1");
      } catch {
        continue;
      }
      capture("setup_step_done", { step: id, via: "app", next_step: NEXT[id] });
    }
    const allDone = steps.length > 0 && steps.every((s) => s.state === "done");
    if (allDone && claimOnboardingCompleted(orgId)) {
      capture("onboarding_completed", {
        connector_count: done.connectorCount,
        customer_connected: true,
        customer_connections: done.customerConnections,
        seconds_in_visit: null,
        finished_in: "platform",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneKey, loading, orgId, status]);
}
